export const TEMPLATE_IMAGE_FETCH_LIMIT = 10;

const getTemplateRefs = (formApi, fallbackRefs = []) => {
  const normalizedFallbackRefs = Array.isArray(fallbackRefs) ? fallbackRefs.filter(Boolean) : [];
  if (!formApi) return normalizedFallbackRefs;

  const formState = formApi.getState ? formApi.getState() : {};
  const currentValues = formState.values || {};
  const initialValues = formState.initialValues || {};

  if (Array.isArray(currentValues.templateImageRefs)) {
    return currentValues.templateImageRefs.filter(Boolean);
  }

  if (Array.isArray(initialValues.templateImageRefs)) {
    return initialValues.templateImageRefs.filter(Boolean);
  }

  return normalizedFallbackRefs;
};

export const isTemplateImageSelected = (formApi, templateImageId, fallbackRefs = []) => {
  if (!templateImageId) return false;
  const refs = getTemplateRefs(formApi, fallbackRefs);
  return refs.some(ref => ref?.templateImageId === templateImageId);
};

export const getAvailableTemplateImages = (stockImages, formApi, fallbackRefs = []) => {
  if (!Array.isArray(stockImages)) return [];
  const refs = getTemplateRefs(formApi, fallbackRefs);
  const selectedIds = new Set(refs.map(ref => ref?.templateImageId).filter(Boolean));
  return stockImages.filter(img => !selectedIds.has(img?.id));
};

export const appendTemplateImageRef = (formApi, uploadedImageIdUuid, template, fallbackRefs = []) => {
  if (!formApi || !uploadedImageIdUuid || !template) return;

  const existingRefs = getTemplateRefs(formApi, fallbackRefs);

  if (
    existingRefs.some(
      ref =>
        ref?.sharetribeImageId === uploadedImageIdUuid ||
        ref?.templateImageId === template.id
    )
  ) {
    return;
  }

  formApi.change('templateImageRefs', [
    ...existingRefs,
    {
      sharetribeImageId: uploadedImageIdUuid,
      cloudinaryUrl: template.image_url,
      templateImageId: template.id,
      source: template.source || 'admin',
    },
  ]);
};

const appendUploadedImageToForm = (formApi, uploadedImage) => {
  if (!formApi || !uploadedImage) return;

  const formState = formApi.getState ? formApi.getState() : {};
  const currentValues = formState.values || {};
  const currentImages = Array.isArray(currentValues.images) ? currentValues.images : [];
  const dirtyFields = formState.dirtyFields || {};
  const modified = formState.modified || {};
  const imagesFieldDirty = !!dirtyFields.images || !!modified.images;
  const uploadedUuid = uploadedImage?.imageId?.uuid || uploadedImage?.id?.uuid;
  if (!uploadedUuid || !imagesFieldDirty) return;

  const alreadyExists = currentImages.some(img => {
    const imageUuid = img?.imageId?.uuid || img?.id?.uuid;
    return imageUuid === uploadedUuid;
  });

  if (alreadyExists) return;

  formApi.change('images', [...currentImages, uploadedImage]);
};

export const removeTemplateImageRef = (formApi, removedImage, fallbackRefs = []) => {
  if (!formApi || !removedImage) return;

  const removedUuid =
    (removedImage && typeof removedImage === 'object' && removedImage?.imageId?.uuid) ||
    (removedImage && typeof removedImage === 'object' && removedImage?.id?.uuid) ||
    (removedImage && typeof removedImage === 'object' && removedImage?.uuid) ||
    null;
  if (!removedUuid) return;

  const existingRefs = getTemplateRefs(formApi, fallbackRefs);
  if (existingRefs.length === 0) return;

  formApi.change(
    'templateImageRefs',
    existingRefs.filter(ref => ref?.sharetribeImageId !== removedUuid)
  );
};

export const useTemplateImage = ({
  image,
  imageUploadRequested,
  templateUploadInProgressId,
  isAtLimit,
  onUseTemplateImage,
  listingImageConfig,
  onStart,
  onSuccess,
  onError,
}) => {
  if (!image || templateUploadInProgressId || imageUploadRequested) return Promise.resolve(null);
  if (isAtLimit) {
    if (onError) onError('limit');
    return Promise.resolve(null);
  }
  if (!onUseTemplateImage) return Promise.resolve(null);

  if (onStart) onStart(image);

  return onUseTemplateImage(image, listingImageConfig)
    .then(uploaded => {
      const uploadedImage = uploaded?.payload || uploaded;
      const uploadedImageIdUuid = uploadedImage?.imageId?.uuid || uploadedImage?.id?.uuid;
      if (onSuccess) onSuccess(uploadedImageIdUuid, image, uploadedImage);
      return uploadedImage;
    })
    .catch(error => {
      const details =
        (error && error.data && JSON.stringify(error.data)) ||
        (error && error.apiResponse && JSON.stringify(error.apiResponse)) ||
        (error && error.response && error.response.data && JSON.stringify(error.response.data)) ||
        (error && error.message) ||
        String(error);
      const errorKey =
        details && String(details).includes('download_failed') ? 'download_failed' : 'upload_failed';
      if (onError) onError(errorKey, details, error);
      throw error;
    });
};

export const createUseTemplateImageHandler = ({
  formApi,
  imageUploadRequested,
  templateUploadInProgressId,
  isAtLimit,
  onUseTemplateImage,
  listingImageConfig,
  setImageUploadRequested,
  setTemplateUploadError,
  setTemplateUploadErrorDetails,
  setTemplateUploadInProgressId,
  setTemplateUploadPhase,
  closeTemplatePreview,
  existingTemplateRefs = [],
}) => {
  return image => {
    if (isTemplateImageSelected(formApi, image?.id, existingTemplateRefs)) {
      setTemplateUploadError(null);
      setTemplateUploadErrorDetails(null);
      return;
    }

    useTemplateImage({
      image,
      imageUploadRequested,
      templateUploadInProgressId,
      isAtLimit,
      onUseTemplateImage,
      listingImageConfig,
      onStart: templateImage => {
        setTemplateUploadError(null);
        setTemplateUploadErrorDetails(null);
        setTemplateUploadInProgressId(templateImage.id);
        setTemplateUploadPhase('downloading');
        setImageUploadRequested(true);
      },
      onSuccess: (uploadedImageIdUuid, templateImage, uploadedImage) => {
        appendUploadedImageToForm(formApi, uploadedImage);
        appendTemplateImageRef(formApi, uploadedImageIdUuid, templateImage, existingTemplateRefs);
        setImageUploadRequested(false);
        setTemplateUploadInProgressId(null);
        setTemplateUploadPhase(null);
        if (closeTemplatePreview) {
          closeTemplatePreview();
        }
      },
      onError: (errorKey, details) => {
        setImageUploadRequested(false);
        setTemplateUploadInProgressId(null);
        setTemplateUploadPhase(null);
        setTemplateUploadError(errorKey);
        setTemplateUploadErrorDetails(details || null);
      },
    }).catch(() => {});
  };
};
